import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Bell, Mail, MessageSquare, Loader2, FileText, KeyRound, BarChart3 } from 'lucide-react';
import { useToast } from '../components/ui/Toast';
type SettingsCategory = 'notifications' | 'templates';
export function Settings() {
  const [activeCategory, setActiveCategory] = useState<SettingsCategory>('notifications');
  const [notificationTab, setNotificationTab] = useState<'email' | 'sms'>('email');
  const [loadingSection, setLoadingSection] = useState<string | null>(null);
  const {
    showToast
  } = useToast();
  const handleSaveSettings = async (section: string) => {
    setLoadingSection(section);
    await new Promise(resolve => setTimeout(resolve, 800));
    showToast({
      type: 'success',
      title: 'Settings Saved',
      message: `${section.charAt(0).toUpperCase() + section.slice(1)} settings saved successfully.`
    });
    setLoadingSection(null);
  };
  const categories = [{
    id: 'notifications' as const,
    label: 'Notifications',
    icon: Bell,
    color: 'bg-indigo-100 text-indigo-600'
  }, {
    id: 'templates' as const,
    label: 'Email Templates',
    icon: FileText,
    color: 'bg-amber-100 text-amber-600'
  }];
  return <div className="space-y-6 pb-12" data-id="element-1020">
      {/* Mobile Header */}
      <div className="md:hidden" data-id="element-1021">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-1022">Settings</h1>
        <p className="text-sm text-slate-500 mt-1" data-id="element-1023">System configuration</p>
      </div>

      {/* Mobile Category Selector */}
      <div className="md:hidden overflow-x-auto -mx-4 px-4" data-id="element-1024">
        <div className="flex gap-2 pb-2" data-id="element-1025">
          {categories.map(category => {
          const Icon = category.icon;
          const isActive = activeCategory === category.id;
          return <button key={category.id} onClick={() => setActiveCategory(category.id)} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${isActive ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-slate-600 border border-slate-200'}`} data-id="element-1026">
                <Icon className="w-4 h-4" data-id="element-1027" />
                <span data-id="element-1028">{category.label}</span>
              </button>;
        })}
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="flex gap-6" data-id="element-1029">
        {/* Sidebar */}
        <div className="hidden md:block w-64 flex-shrink-0" data-id="element-1030">
          <div className="sticky top-6" data-id="element-1031">
            <div className="mb-6" data-id="element-1032">
              <h1 className="text-xl font-bold text-slate-900" data-id="element-1033">Settings</h1>
              <p className="text-sm text-slate-500 mt-1" data-id="element-1034">
                System configuration
              </p>
            </div>
            <nav className="space-y-1" data-id="element-1035">
              {categories.map(category => {
              const Icon = category.icon;
              const isActive = activeCategory === category.id;
              return <button key={category.id} onClick={() => setActiveCategory(category.id)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${isActive ? 'bg-blue-50 text-blue-700 shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-1036">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? category.color : 'bg-slate-100 text-slate-400'}`} data-id="element-1037">
                      <Icon className="w-4 h-4" data-id="element-1038" />
                    </div>
                    <span data-id="element-1039">{category.label}</span>
                  </button>;
            })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0" data-id="element-1040">
          {/* ========== NOTIFICATIONS ========== */}
          {activeCategory === 'notifications' && <Card data-id="element-1041">
              <CardHeader data-id="element-1042">
                <CardTitle data-id="element-1043">Notification Preferences</CardTitle>
                <p className="text-sm text-slate-500 mt-1" data-id="element-1044">
                  Configure notification channels and alert triggers
                </p>
              </CardHeader>
              <CardContent className="space-y-6" data-id="element-1045">
                <div className="flex gap-2 border-b border-slate-200 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0" data-id="element-1046">
                  <button onClick={() => setNotificationTab('email')} className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${notificationTab === 'email' ? 'border-teal-600 text-teal-600' : 'border-transparent text-slate-600 hover:text-slate-900'}`} data-id="element-1047">
                    <Mail className="w-4 h-4 inline mr-2" data-id="element-1048" />
                    Email / SMTP
                  </button>
                  <button onClick={() => setNotificationTab('sms')} className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${notificationTab === 'sms' ? 'border-teal-600 text-teal-600' : 'border-transparent text-slate-600 hover:text-slate-900'}`} data-id="element-1049">
                    <MessageSquare className="w-4 h-4 inline mr-2" data-id="element-1050" />
                    SMS
                  </button>
                </div>

                {notificationTab === 'email' && <div className="space-y-6" data-id="element-1051">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1052">
                      <Input label="SMTP Host" placeholder="smtp.example.com" defaultValue="smtp.gmail.com" data-id="element-1053" />
                      <Input label="SMTP Port" type="number" placeholder="587" defaultValue="587" data-id="element-1054" />
                      <Input label="Username / Email" type="email" placeholder="notifications@example.com" defaultValue="notifications@waseel.com" data-id="element-1055" />
                      <Input label="Password" type="password" placeholder="••••••••" data-id="element-1056" />
                      <Input label="From Address" type="email" placeholder="noreply@example.com" defaultValue="noreply@waseel.com" data-id="element-1057" />
                      <Input label="From Name" placeholder="Company Name" defaultValue="Waseel Jisr" data-id="element-1058" />
                    </div>
                  </div>}

                {notificationTab === 'sms' && <div className="space-y-6" data-id="element-1059">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1060">
                      <Select label="SMS Provider" options={[{
                  value: 'twilio',
                  label: 'Twilio'
                }, {
                  value: 'aws-sns',
                  label: 'AWS SNS'
                }, {
                  value: 'nexmo',
                  label: 'Vonage (Nexmo)'
                }]} defaultValue="twilio" data-id="element-1061" />
                      <Input label="Account SID / Username" placeholder="AC1234567890abcdef" data-id="element-1062" />
                      <Input label="Auth Token / API Key" type="password" placeholder="••••••••••••••••" data-id="element-1063" />
                      <Input label="Sender ID / Phone Number" placeholder="+966501234567" defaultValue="+966501234567" data-id="element-1064" />
                    </div>
                  </div>}

                <div className="flex justify-end pt-2 border-t border-slate-100" data-id="element-1065">
                  <Button onClick={() => handleSaveSettings('notifications')} disabled={loadingSection === 'notifications'} data-id="element-1066">
                    {loadingSection === 'notifications' ? <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" data-id="element-1067" />
                        Saving...
                      </> : 'Save Changes'}
                  </Button>
                </div>
              </CardContent>
            </Card>}

          {/* ========== EMAIL TEMPLATES ========== */}
          {activeCategory === 'templates' && <div className="space-y-6" data-id="element-1068">
              <Card data-id="element-1069">
                <CardHeader data-id="element-1070">
                  <CardTitle data-id="element-1071">Email Templates</CardTitle>
                  <p className="text-sm text-slate-500 mt-1" data-id="element-1072">
                    Customize the email layout for notifications and reports
                  </p>
                </CardHeader>
                <CardContent className="space-y-6" data-id="element-1073">
                  <div className="flex gap-2 border-b border-slate-200" data-id="element-1074">
                    <button className="px-4 py-2 text-sm font-medium transition-colors border-b-2 whitespace-nowrap border-teal-600 text-teal-600" data-id="element-1075">
                      <KeyRound className="w-4 h-4 inline mr-2" data-id="element-1076" />
                      OTP / Password Reset
                    </button>
                    <button className="px-4 py-2 text-sm font-medium transition-colors border-b-2 whitespace-nowrap border-transparent text-slate-600 hover:text-slate-900" data-id="element-1077">
                      <BarChart3 className="w-4 h-4 inline mr-2" data-id="element-1078" />
                      Report Template
                    </button>
                  </div>

                  <div className="space-y-4" data-id="element-1079">
                    <Input label="Email Subject" defaultValue="Your OTP Code — Waseel Password Reset" data-id="element-1080" />
                    <Input label="Email Heading" defaultValue="Password Reset OTP" data-id="element-1081" />
                    <div className="space-y-1.5" data-id="element-1082">
                      <label className="text-sm font-medium text-slate-700" data-id="element-1083">
                        Email Body
                      </label>
                      <textarea className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 min-h-[180px] font-mono resize-y" defaultValue="Hello,\n\nYou have requested to reset your password. Please use the following one-time password (OTP) to proceed:\n\nYour OTP: {{otp_code}}\n\nThis code will expire in {{expiry_minutes}} minutes.\n\nIf you did not request a password reset, please ignore this email or contact your administrator immediately." data-id="element-1084" />
                    </div>
                    <Input label="Email Footer" defaultValue="© {{year}} Waseel Jisr Portal. All rights reserved." data-id="element-1085" />
                  </div>

                  <div className="flex items-center justify-end pt-2 border-t border-slate-100" data-id="element-1086">
                    <Button onClick={() => handleSaveSettings('templates')} disabled={loadingSection === 'templates'} data-id="element-1087">
                      {loadingSection === 'templates' ? <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" data-id="element-1088" />
                          Saving...
                        </> : 'Save Template'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>}
        </div>
      </div>
    </div>;
}